import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import LanguageToggle from './LanguageToggle';

const Header = ({ t, currentLang, onLanguageToggle }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    // If we're not on the home page, navigate home with the hash;
    // the home page reads the hash on load and scrolls to the section.
    if (window.location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#FAF9F7] shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center">
            <button
              onClick={() => scrollToSection('hero')}
              className="text-2xl font-serif text-[#2C2C2C] hover:text-[#8B4513] transition-colors duration-300"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Glow & You
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <button
              onClick={() => scrollToSection('hero')}
              className="nav-link text-sm uppercase tracking-wider text-[#2C2C2C] hover:text-[#8B4513] transition-colors duration-300"
            >
              {t.nav.home}
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="nav-link text-sm uppercase tracking-wider text-[#2C2C2C] hover:text-[#8B4513] transition-colors duration-300"
            >
              {t.nav.services}
            </button>
            <a
              href="/temoignages"
              className="nav-link text-sm uppercase tracking-wider text-[#2C2C2C] hover:text-[#8B4513] transition-colors duration-300"
            >
              {t.nav.testimonials}
            </a>
            <button
              onClick={() => scrollToSection('about')}
              className="nav-link text-sm uppercase tracking-wider text-[#2C2C2C] hover:text-[#8B4513] transition-colors duration-300"
            >
              {t.nav.about}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="nav-link text-sm uppercase tracking-wider text-[#2C2C2C] hover:text-[#8B4513] transition-colors duration-300"
            >
              {t.nav.contact}
            </button>
            <LanguageToggle currentLang={currentLang} onToggle={onLanguageToggle} />
          </nav>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-4">
            <LanguageToggle currentLang={currentLang} onToggle={onLanguageToggle} />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-[#2C2C2C] hover:text-[#8B4513] transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="md:hidden py-4 bg-[#FAF9F7] border-t border-[#D4C4B0]">
            <div className="flex flex-col gap-4">
              <button
                onClick={() => scrollToSection('hero')}
                className="text-left px-4 py-2 text-sm uppercase tracking-wider text-[#2C2C2C] hover:text-[#8B4513] transition-colors"
              >
                {t.nav.home}
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className="text-left px-4 py-2 text-sm uppercase tracking-wider text-[#2C2C2C] hover:text-[#8B4513] transition-colors"
              >
                {t.nav.services}
              </button>
              <a
                href="/temoignages"
                className="text-left px-4 py-2 text-sm uppercase tracking-wider text-[#2C2C2C] hover:text-[#8B4513] transition-colors"
              >
                {t.nav.testimonials}
              </a>
              <button
                onClick={() => scrollToSection('about')}
                className="text-left px-4 py-2 text-sm uppercase tracking-wider text-[#2C2C2C] hover:text-[#8B4513] transition-colors"
              >
                {t.nav.about}
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-left px-4 py-2 text-sm uppercase tracking-wider text-[#2C2C2C] hover:text-[#8B4513] transition-colors"
              >
                {t.nav.contact}
              </button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
